// PURE camera maths (§2.9): world cell = CELL_WU units; camera origin (0,0); screen = zoom·(world − scroll),
// all in canvas backing-store px (CSS px · dprEff). M5: the board shows the expansion mask, so world cell
// `layout.origin` lands on layout.board's top-left; an expansion tweens between two fits (600 ms).
import type { Layout } from '../layout/computeLayout.ts';

export interface CameraFit {
  zoom: number;
  scrollX: number;
  scrollY: number;
}

/** zoom = cellCss·dprEff/CELL_WU; scroll puts world cell `layout.origin` on layout.board's top-left. */
export function cameraFor(layout: Layout, dprEff: number, cellWu: number): CameraFit {
  const zoom = (layout.cellCss * dprEff) / cellWu;
  return {
    zoom,
    scrollX: layout.origin.x * cellWu - (layout.board.x * dprEff) / zoom,
    scrollY: layout.origin.y * cellWu - (layout.board.y * dprEff) / zoom,
  };
}

/** World units → client CSS px through a fit (inverse of the camera; used by tests and hit maths). */
export function worldToClient(
  fit: CameraFit,
  dprEff: number,
  wx: number,
  wy: number,
): { x: number; y: number } {
  return {
    x: (fit.zoom * (wx - fit.scrollX)) / dprEff,
    y: (fit.zoom * (wy - fit.scrollY)) / dprEff,
  };
}

/**
 * The camera between two fits at progress t ∈ [0, 1] (expansion tween, eased by the caller). The zoom is
 * interpolated geometrically and the world point under the board's top-left moves linearly, so the board
 * grows smoothly without the corner swinging out.
 */
export function lerpFit(a: CameraFit, b: CameraFit, t: number): CameraFit {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const zoom = a.zoom * (b.zoom / a.zoom) ** t;
  return {
    zoom,
    scrollX: a.scrollX + (b.scrollX - a.scrollX) * t,
    scrollY: a.scrollY + (b.scrollY - a.scrollY) * t,
  };
}

/** Quad.out easing. */
export function easeOutQuad(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return 1 - (1 - c) * (1 - c);
}

/**
 * The frame-clock tweens' easing by its juice.json name (Phaser's `Family.dir` names; the camera uses
 * `easing.out`). Families: Linear, Quad, Cubic, Quart, Sine, Expo; directions in / out / inOut. An unknown name
 * throws, so a data typo fails loudly instead of snapping.
 */
export function easeByName(name: string, t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  if (name === 'Linear') return c;
  const [fam, dir] = name.split('.');
  const inF = EASE_IN[fam ?? ''];
  if (!inF || (dir !== 'in' && dir !== 'out' && dir !== 'inOut'))
    throw new RangeError(`unknown easing ${name}`);
  if (dir === 'in') return inF(c);
  if (dir === 'out') return 1 - inF(1 - c);
  return c < 0.5 ? inF(2 * c) / 2 : 1 - inF(2 * (1 - c)) / 2;
}

const EASE_IN: Readonly<Record<string, (t: number) => number>> = {
  Quad: (t) => t * t,
  Cubic: (t) => t * t * t,
  Quart: (t) => t * t * t * t,
  Sine: (t) => 1 - Math.cos((t * Math.PI) / 2),
  Expo: (t) => (t === 0 ? 0 : 2 ** (10 * t - 10)),
};

/**
 * The world cells a `w` × `h` CSS-px viewport shows through `layout` (inclusive, one cell of margin each side):
 * the rock skirt round the world grid covers them (desktop: the mine round the column, not the bare page).
 */
export function viewCells(
  layout: Layout,
  w: number,
  h: number,
): { x0: number; y0: number; x1: number; y1: number } {
  const c = Math.max(1, layout.cellCss);
  return {
    x0: Math.floor(layout.origin.x - layout.board.x / c) - 1,
    y0: Math.floor(layout.origin.y - layout.board.y / c) - 1,
    x1: Math.ceil(layout.origin.x + (w - layout.board.x) / c) + 1,
    y1: Math.ceil(layout.origin.y + (h - layout.board.y) / c) + 1,
  };
}
